
import React from "react";
import { format, parseISO } from "date-fns";
import { Calendar, Clock, Users } from "lucide-react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BookingType } from "@/lib/supabase/types";
import PaymentStatusBadge from "./PaymentStatusBadge";
import BookingActions from "./BookingActions";

interface BookingGroup {
  classId: string;
  className: string;
  classDay: string;
  classTime: string;
  classDate?: string;
  bookings: BookingType[];
}

interface BookingGroupCardProps {
  group: BookingGroup;
  showActions?: boolean;
  onReschedule?: (booking: BookingType) => void;
  onDelete?: (bookingId: string, classId: string) => void;
  onUpdatePaymentStatus?: (bookingId: string, paymentStatus: 'pending' | 'paid' | 'nptr') => void;
}

const BookingGroupCard: React.FC<BookingGroupCardProps> = ({
  group,
  showActions = true,
  onReschedule,
  onDelete,
  onUpdatePaymentStatus,
}) => {
  return (
    <Card key={group.classId} className="overflow-hidden mb-6">
      <CardHeader className="bg-muted/50">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>{group.className}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <Calendar className="h-4 w-4" /> 
              {group.classDate ? format(parseISO(group.classDate), 'EEEE, MMMM d, yyyy') : group.classDay}
              <Clock className="h-4 w-4 ml-2" /> {group.classTime}
            </CardDescription>
          </div>
          <div className="bg-primary/10 px-3 py-1 rounded-full text-sm font-medium">
            {group.bookings.length} {group.bookings.length === 1 ? 'Booking' : 'Bookings'}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Group Size</TableHead>
              <TableHead>Date Booked</TableHead>
              <TableHead>Payment</TableHead>
              {showActions && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.bookings.map((booking: BookingType) => (
              <TableRow key={booking.id}>
                <TableCell>{`${booking.firstName} ${booking.lastName}`}</TableCell>
                <TableCell>{booking.email}</TableCell>
                <TableCell>{booking.phone}</TableCell>
                <TableCell>
                  {booking.groupSize && booking.groupSize > 1 ? (
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                      <Users className="h-3 w-3 mr-1" /> 
                      {booking.groupSize}
                    </Badge>
                  ) : (
                    "1"
                  )}
                </TableCell>
                <TableCell>
                  {booking.createdAt ? format(new Date(booking.createdAt), 'MMM d, yyyy') : '-'}
                </TableCell>
                <TableCell>
                  <PaymentStatusBadge 
                    booking={booking} 
                    onUpdatePaymentStatus={onUpdatePaymentStatus} 
                  />
                </TableCell>
                {showActions && (
                  <TableCell>
                    <BookingActions 
                      booking={booking}
                      showActions={showActions}
                      onReschedule={onReschedule}
                      onDelete={onDelete}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default BookingGroupCard;
